import { useState } from "react";
import { toast } from "react-toastify";
import { notifySuccess } from "../../../../../../assets/toastService";
import { isValidEmail } from "../../../../../utils/removeEmoji";
import { CustomerData } from "src/shared/services/interfacev2";
import { postCustomer } from "src/shared/services/servicesv2";

interface UseCustomerFormProps {
  onSuccess: (customer: CustomerData) => void; // Called after successful add
  onBack: () => void;
}

export const useCustomerForm = ({ onSuccess, onBack }: UseCustomerFormProps) => {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    alternativePhone: "",
    email: "",
    phones: [],
    emails: [],
    address: "",
    address2: "",
    address3: "",
    address4: "",
    osb: "",
  });
  const [emailError, setEmailError] = useState(false);
  const [errorEmailExists, setErrorEmailExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCustomer = async () => {
    // const regex = /^[a-zA-Z0\s]*$/;
    // if (!regex.test(formData.name)) {
    //   toast.error("Special characters are not allowed in name.");
    //   return;
    // }
    if (!formData.firstName || !formData.phone) {
      toast.error("Please fill in required fields.");
      return;
    }

    setSubmitting(true);

    const customerData = {
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      phones: [formData.phone, formData.alternativePhone].filter(Boolean),
      emails: [formData.email].filter(Boolean),
      openingOutstandingBalance: Number(formData.osb) ?? 0.0,
      addresses: {
        addressLine1: formData.address,
        addressLine2: formData.address2,
        city: formData.address3,
        postalCode: formData.address4,
      },
    };

    try {
      const res = await postCustomer(customerData);
      const created: CustomerData = res.data.customer as CustomerData;
      notifySuccess("Customer added successfully");
      onSuccess(created);
      setTimeout(onBack, 500);
    } catch (err: any) {
      const message = err?.response?.data?.message || "Something went wrong";
      if (message === "Alternative number must be different from phone number") {
        toast.error("Alternative number must be different from phone number");
      } else if (message.includes("Phone Number")) {
        toast.error("Phone number already exists.");
      } else if (message.includes("Alternate") || message.includes("Alternative")) {
        toast.error("Alternate phone number already exists.");
      } else if (message.includes("Email")) {
        setEmailError(true);
        setErrorEmailExists(true);
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateEmail = (email: string, resetExists?: boolean) => {
    if (resetExists) setErrorEmailExists(false); // reset "email already exists"
    setEmailError(!isValidEmail(email) && email !== "");
  };

  const resetForm = () => {
    setFormData({ firstName: "", middleName: "", lastName: "", phone: "", alternativePhone: "", email: "", phones: [], emails: [], address: "", address2: "", address3: "", address4: "", osb: "" });
    setEmailError(false);
    setErrorEmailExists(false);
  };

  return {
    formData,
    emailError,
    errorEmailExists,
    submitting,
    handleInputChange,
    handleAddCustomer,
    validateEmail,
    resetForm,
  };
};
